/**
 * 사이트 설정 키
 * @description 사이트 설정에 대한 값을 api로 조회할 때 필요한 키 입니다.
 * 젭스 어드민의 키와 맞춰주세요. 어드민에서 정의하지 않은 키는 요청해도 무시됩니다.
 */
export enum SITE_CONFIG_KEY {
  CHANNEL_TALK_URL = 'channel_talk_url', // 젭스 채널톡 주소
  COMPANY_INFO = 'company_info', // 사업자 정보 (CompanyInfo JSON)
  CS_INFO = 'cs_info', // 고객센터(Customer Service) 정보 (CsInfo JSON)
  FREE_TRIAL_BLOG_REVIEW = 'free_trial_blog_review', // 무료체험 블로그 후기 링크
  FREE_TRIAL_PRIMARY_BLOG_REVIEW = 'free_trial_primary_blog_review', // 무료체험 블로그 대표 후기 링크
  FREE_TRIAL_YOUTUBE_REVIEW = 'free_trial_youtube_review', // 무료체험 유튜브 후기 링크
  FREE_TRIAL_PRIMARY_YOUTUBE_REVIEW = 'free_trial_primary_youtube_review', // 무료체험 유튜브 대표 후기 링크
  FREE_TRIAL_SEMINAR_APPLY = 'free_trial_seminar_apply', // 무료체험 설명회 신청 링크
  FREE_TRIAL_SEMINAR_VIDEO = 'free_trial_seminar_video', // 무료체험 설명회 영상 링크
  FREE_TRIAL_KAKAO = 'free_trial_kakao', // 무료체험 카카오톡 알림 링크
  JEBS_REVIEW_BLOG_MORE = 'jebs_review_blog_more', // 블로그 후기 더보기 링크
  JEBS_REVIEW_VIDEOS = 'jebs_review_videos', //영상 후기 목록
  JEBS_REVIEW_VIDEO_MORE = 'jebs_review_video_more', // 영상 후기 더보기 링크
  JEBS_STORY_METHOD_TEXT = 'jebs_story_method_text', // 젭스 스토리 삽화기억술 문구 (일반 텍스트)
  JEBS_STORY_METHOD_VIDEO = 'jebs_story_method_video', // 젭스 스토리 삽화기억술 영상 URL
  JEBS_STORY_MISSION_TEXT = 'jebs_story_mission_text', // 젭스 스토리 사명 문구 (StoryMissionText JSON)
  JEBS_STORY_MISSION_VIDEO = 'jebs_story_mission_video', // 젭스 스토리 사명 영상 URL
  JEBS_STORY_TEACHER_REVIEW_LIST = 'jebs_story_teacher_review_list', // 젭스 스토리 선생님 후기 목록 (TeacherReviewItem[] JSON)
  JEBS_STORY_TEACHER_REVIEW_TEXT = 'jebs_story_teacher_review_text', // 젭스 스토리 선생님 후기 문구 (일반 텍스트)
  JEBS_STORY_TIMELINE_MILESTONES = 'jebs_story_timeline_milestones', // 젭스 스토리 연혁 (SiteConfigItem[] JSON)
  JEBS_STORY_TIMELINE_STATS = 'jebs_story_timeline_stats', // 젭스 스토리 실적 수치 (StoryTimelineStats JSON)
  JEBS_STORY_TIMELINE_TEXT = 'jebs_story_timeline_text', // 젭스 스토리 타임라인 헤드라인 문구 (일반 텍스트)
  LEARNING_GUIDE = 'learning_guide', // 내 학습 (교사대학) 이용 메뉴얼 링크
  // 약관(term_*)은 /v1/site-terms 로 이전됨 (site-terms-type 의 SITE_TERM_KEY)
}

/** 고객센터 정보(cs_info) */
export interface CsInfo {
  phone: string; // 대표 연락처
  email: string; // 대표 이메일
  hours: string; // 상담 지원 시간
}

/**
 * 목록형 설정 값의 아이템
 * @description 연혁, 지표처럼 목록인 설정은 이 배열의 JSON 문자열로 저장된다.
 * 주소, 연락처처럼 단일 값인 설정은 문자열 그대로 내려오므로 파싱하지 않는다.
 */
export interface SiteConfigItem {
  label: string;
  value: string;
}

/** 젭스 스토리 실적 수치(jebs_story_timeline_stats) - 기준일과 수치 목록을 함께 내려준다 */
export interface StoryTimelineStats {
  asOf: string;
  items: SiteConfigItem[];
}

/** 사업자 정보(company_info) - 푸터에 표시 */
export interface CompanyInfo {
  name: string; // 상호
  address: string;
  ceo: string; // 대표자명
  businessNumber: string; // 사업자등록번호
  mailOrderNumber: string; // 통신판매업신고번호
  copyright: string; // ⓒ 뒤에 붙는 저작권자 표기
}

/**
 * 젭스 스토리 사명 문구(jebs_story_mission_text)
 * @description 줄바꿈은 \n 으로 입력한다 (HTML 태그 사용 불가)
 */
export interface StoryMissionText {
  title: string;
  subtitle: string;
  description: string;
}

/** 선생님 후기 목록(jebs_story_teacher_review_list) 아이템 */
export interface TeacherReviewItem {
  name: string;
  hashtags: string[];
  photoUrl: string;
  videoUrl: string;
}

// 설정 조회 응답 (value 는 파싱 전 원본 문자열)
export interface SiteConfigResponse {
  key: string;
  value: string;
}

// 키별 원본 문자열 맵 (목록형은 사용처에서 parseSiteConfigValue 로 파싱)
export type SiteConfigMap = Partial<Record<SITE_CONFIG_KEY, string>>;
